import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ImpersonationViewType } from 'constants/impersonation';
import { HoldingType } from 'types/holding';

export interface UserType {
  user_id: number;
  name: string;
  FullName: string;
  fname: string;
  email: string;
  isSuperuser: boolean;
  isStaff: boolean;
  groups: any[];
  company?: number;
  companyPrimaryEmail?: string;
  holding?: HoldingType;
  level?: string;
  avatar?: string;
  companyName?: string;
  PAUser_Id: any;
  all_permissions: string[];
  pending_password_reset?: boolean;
  feature_flags?: {
    show_beta_settings: boolean;
    show_ai_recommendations: boolean;
    show_sales_battlecard_content: boolean;
    show_ai_competitive_intelligence: boolean;
    show_ai_speed_to_lead: boolean;
    show_user_management: boolean;
    show_sales_talk_tracks: boolean;
    show_action_plans: boolean;
    show_action_plans_company: boolean;
    show_alerts: boolean;
    show_marketing_campaigns: boolean;
    show_shopper_communication: boolean;
    view_pricing_data: boolean;
    show_ringcentral_records: boolean;
    show_action_center: boolean;
    show_cmi_digest: boolean;
    show_ai_company_overview: boolean;
    show_coming_soon_features: boolean;
  };
  cmi_digest_preference?: {
    enabled: boolean;
    frequency: string;
    competitor_incentives_enabled: boolean;
    community_reviews_enabled: boolean;
  };
  manager?: {
    id: number;
    name: string;
    email: string;
    is_superuser: boolean;
    is_staff: boolean;
    is_active?: boolean;
    company?: number;
    level?: string;
  };
  // Set by the backend while a superuser supports this user, null otherwise
  current_view?: ImpersonationViewType | null;
}

interface AuthType {
  currentUser: UserType | null;
  refreshToken: string;
  accessToken: string;
}

// Define the initial state using that type
const initialState: AuthType = {
  currentUser: null,
  refreshToken: '',
  accessToken: '',
};

export const authSlice = createSlice({
  name: 'auth',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    handleLogin: (state, action: PayloadAction<AuthType>) => {
      state.currentUser = action.payload.currentUser;
      state.refreshToken = action.payload.refreshToken;
      state.accessToken = action.payload.accessToken;
    },
    handleUpdateAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    handleUpdateUser: (state, action: PayloadAction<UserType>) => {
      state.currentUser = action.payload;
    },
    handleSwitchCurrentView: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string; currentView: ImpersonationViewType }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      if (state.currentUser) {
        state.currentUser.current_view = action.payload.currentView;
      }
    },
    handleUpdateAvatar: (state, action: PayloadAction<string>) => {
      if (state.currentUser) {
        state.currentUser = {
          ...state.currentUser,
          avatar: action.payload,
        };
      }
    },
    handleUpdateCmiDigestPreferenceEnabled: (state, action: PayloadAction<boolean>) => {
      if (state.currentUser?.cmi_digest_preference) {
        state.currentUser.cmi_digest_preference.enabled = action.payload;
      }
    },

    handleLogout: (state) => {
      state.currentUser = null;
      state.refreshToken = '';
      state.accessToken = '';
    },
  },
});

export const {
  handleLogin,
  handleLogout,
  handleUpdateAccessToken,
  handleUpdateUser,
  handleSwitchCurrentView,
  handleUpdateAvatar,
  handleUpdateCmiDigestPreferenceEnabled,
} = authSlice.actions;

export default authSlice.reducer;
